import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ChangeEvent } from 'react'

import { ApiError } from '../api/client'
import { endpoints } from '../api/endpoints'
import {
  decodePicture,
  fromBase64,
  isBlank,
  toMono,
  toPayload,
  toRgba,
  type Decoder,
  type DecodedPicture,
  type LogoMode,
  type Mono,
} from '../logo'
import { errorMessage, t } from '../strings'

const MAX_FILE_BYTES = 5 * 1024 * 1024

/** Draws the dots at their real size, so the owner sees what the printer will print. */
function DotsPreview({ mono }: { mono: Mono }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    // No canvas in some environments (tests): the preview is then simply not drawn.
    const ctx = ref.current?.getContext?.('2d')
    if (!ctx) return
    ctx.putImageData(new ImageData(toRgba(mono) as Uint8ClampedArray<ArrayBuffer>, mono.width, mono.height), 0, 0)
  }, [mono])
  return <canvas ref={ref} className="logo-dots" width={mono.width} height={mono.height} role="img" aria-label={t.logoPreview} />
}

/**
 * Receipt logo: pick a picture, see it as black dots at print size, save. The picture is converted here,
 * in the browser, so the server stores a few kilobytes of bits and the phone sends them to the printer as
 * they are (logo.ts). [decode] is a parameter only so tests can run without a canvas.
 */
export function LogoCard({ decode = decodePicture }: { decode?: Decoder }) {
  const qc = useQueryClient()
  const saved = useQuery({ queryKey: ['logo'], queryFn: endpoints.logo })
  const fileInput = useRef<HTMLInputElement>(null)
  const [picture, setPicture] = useState<DecodedPicture | null>(null)
  const [mode, setMode] = useState<LogoMode>('sharp')
  const [invert, setInvert] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  const picked = picture ? toMono(picture.rgba, picture.width, picture.height, mode, invert) : null
  const blank = picked ? isBlank(picked) : false
  const current: Mono | null =
    picked ?? (saved.data ? { width: saved.data.width, height: saved.data.height, bits: fromBase64(saved.data.data) } : null)

  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e instanceof ApiError ? e.code : 'network') })
  const save = useMutation({
    mutationFn: (m: Mono) => endpoints.saveLogo(toPayload(m)),
    onSuccess: (logo) => {
      qc.setQueryData(['logo'], logo)
      setPicture(null)
      setMessage({ kind: 'ok', text: t.logoSaved })
    },
    onError: fail,
  })
  const remove = useMutation({
    mutationFn: endpoints.deleteLogo,
    onSuccess: () => {
      qc.setQueryData(['logo'], null)
      setPicture(null)
      setMessage({ kind: 'ok', text: t.logoRemoved })
    },
    onError: fail,
  })

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = '' // picking the same file again must still fire
    if (!file) return
    setMessage(null)
    if (file.size > MAX_FILE_BYTES) return setMessage({ kind: 'error', text: t.logoTooBig })
    try {
      setPicture(await decode(file))
    } catch {
      setMessage({ kind: 'error', text: t.logoUnreadable })
    }
  }

  return (
    <section className="card">
      <h2>{t.logoTitle}</h2>
      <p className="muted small">{t.logoBody}</p>

      {current ? (
        <div className="logo-paper">
          <DotsPreview mono={current} />
          <div className="muted small">{t.logoSize(current.width, current.height)}</div>
        </div>
      ) : (
        saved.isSuccess && <p className="muted">{t.logoNone}</p>
      )}

      <button type="button" className="secondary" onClick={() => fileInput.current?.click()}>
        {current ? t.logoChange : t.logoPick}
      </button>
      <input
        ref={fileInput}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp"
        aria-label={t.logoPick}
        onChange={(e) => void onFile(e)}
      />

      {picture && (
        <fieldset className="choice">
          <legend>{t.logoModeLabel}</legend>
          {(
            [
              ['sharp', t.logoModeSharp],
              ['photo', t.logoModePhoto],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className={mode === value ? 'choice-option on' : 'choice-option'}>
              <input type="radio" name="logo_mode" value={value} checked={mode === value} onChange={() => setMode(value)} />
              <span>
                <strong>{label}</strong>
              </span>
            </label>
          ))}
          <label className="check">
            <input type="checkbox" checked={invert} onChange={(e) => setInvert(e.target.checked)} />
            {t.logoInvert}
          </label>
        </fieldset>
      )}

      {blank && <p className="error">{t.logoBlank}</p>}
      {picked && (
        <button type="button" disabled={blank || save.isPending} onClick={() => save.mutate(picked)}>
          {t.logoSave}
        </button>
      )}
      {saved.data && !picked && (
        <button type="button" className="secondary" disabled={remove.isPending} onClick={() => remove.mutate()}>
          {t.logoRemove}
        </button>
      )}
      {message && (
        <p className={message.kind === 'ok' ? 'ok' : 'error'} role={message.kind === 'ok' ? 'status' : 'alert'}>
          {message.text}
        </p>
      )}
    </section>
  )
}
